
import { useMutationState } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { DateTime } from 'luxon';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { getClosedDateDescription, TDR } from 'tdr-common';
import { useEventsByRestaurant } from '../../api/getEventsByRestaurant';
import { useEntityWithAvailabilityRestrictingPolicies } from '../../api/getRestaurantBySlug';
import { MUTATION_KEY } from '../../api/releaseCheckoutSession';
import { isDateBeyondHorizon } from '../../helpers/horizon';
import { HASH_ELEMENT_IDS } from '../../common/hashElementIds';
import { analytics, FILTER_CHANGED, PAGE_EXITED_EVENT } from '../../helpers/analytics';
import { useFilterAnalytics } from '../../helpers/analytics/filterContext';
import { getMaxSupportedGroupSize } from '../../helpers/getMaxSupportedGroupSize';
import { getSpecialEventsSectionVisibility } from '../../helpers/getSpecialEventsSectionVisibility';
import { scrollToElement } from '../../helpers/scrollToElement';
import { AvailabilityGuardContext, SearchFilters } from '../../routes/AvailabilityGuard';
import { TableGuardOutletContext } from '../../routes/TableGuard';
import { PastHorizon } from '../__design-system/modal/exit/past-horizon';
import { SameDayBooking } from '../__design-system/modal/exit/same-day-booking';
import { UnavailableDate } from '../__design-system/modal/exit/unavailable-date';
import { isToday, toLocalJsDate } from '../calendar/Calendar.utils';
import { EventCarousel, isEventVisibleForCard } from '../event-carousel/EventCarousel';
import { EventCarouselSkeleton } from '../event-carousel/EventCarouselSkeleton';
import { DirectLGBInboundNotice } from '../large-group-inbound-notice/variants/DirectLGBInboundNotice';
import { AvailabilityLevel } from '../notice/TableInventoryNotice';
import { PartySizeOption } from '../shared/PartySizeSelector';
import { TimeSelectorSkeleton } from '../time-selector/TimeSelectorSkeleton';
import { Subheading } from '../typography/Subheading';
import styles from './SearchContainer.module.scss';
import { TimeSelector } from '../shared/TimeSelector';
import { SidebarHeader } from '../shared/SidebarHeader';
import { IconButton } from '../buttons/IconButton';
import { X } from 'lucide-react';
import { useSessionStorage } from '../../hooks/useSessionStorage';
import { AvailabilityDatePicker } from '../shared/AvailabilityDatePicker';
import { PartySizePicker } from '../shared/PartySizePicker';
import { SEARCH_PARAM_SELECTED_KEY } from '../../common/constants';
import { useTranslation } from 'react-i18next';

type SearchContainerProps = {
	notice?: (noticeType: AvailabilityLevel) => React.ReactNode;
	results: TDR.Availability.searchOutput;
	tableIds: string[];
	footer?: ReactNode;
	isLoading?: boolean;
	groupSizeOptions: PartySizeOption[];
	showSpecialEvents?: boolean;
};
const SearchContainer = ({ notice, results, tableIds, footer, isLoading, groupSizeOptions, showSpecialEvents = true }: SearchContainerProps) => {
	const { t } = useTranslation();
	const filterCtx = useFilterAnalytics('global page');
	const { restaurant, tables, updateSearchParams, searchFilters, featureFlags, setPreviousTime } = useOutletContext<AvailabilityGuardContext>();
	const { table: selectedTable } = useOutletContext<TableGuardOutletContext>();

	const [sameDayBookingOpen, setSameDayBookingOpen] = useState(false);
	const [searchParams] = useSearchParams();
	// Snapshot the URL at mount so the X button restores it as-is, instead of
	// stripping the availability params that were already on the result page.
	const [initialSearch] = useState(() => searchParams.toString());
	const [prevFilters, setPrevFilters] = useState(searchFilters);

	const [noticeType, setNoticeType] = useState<AvailabilityLevel>();
	const [unavailableDateOpen, setUnavailableDateOpen] = useState(false);
	const [pastHorizonOpen, setPastHorizonOpen] = useState(false);
	const [lastUnavailableDateClicked, setLastUnavailableDateClicked] = useState<DateTime>(null);
	const [unavailableMonth, setUnavailableMonth] = useState<string>('');

	const { data: entityWithPolicies } = useEntityWithAvailabilityRestrictingPolicies({
		slug: restaurant.slug,
		id: selectedTable?.id,
		searchParams: searchFilters
	});

	const [showInboundLeadNotice, setShowInboundLeadNotice] = useState(false);
	// const [selectedServiceName, setSelectedServiceName] = useState<string>();
	const [selectedDate, setSelectedDate] = useState<string>();
	const [isSwitchingDate, setIsSwitchingDate] = useState(false);

	const searchContainerRef = useRef<HTMLDivElement>();
	const footerRef = useRef<HTMLElement>();

	const calendarRef = useRef<HTMLDivElement>(null);
	const timeSelectorRef = useRef<HTMLDivElement>(null);
	const groupSizeRef = useRef<HTMLDivElement>(null);


	const { data: events, isLoading: loadingEvents, isError: errorEvents } = useEventsByRestaurant(restaurant.id, { enabled: showSpecialEvents });

	const visibleEvents = useMemo(
		() => (showSpecialEvents ? (events ?? []).filter(isEventVisibleForCard) : []),
		[showSpecialEvents, events]
	);
	const specialEventsVisibility = getSpecialEventsSectionVisibility({
		showSpecialEvents,
		errorEvents: !!errorEvents,
		loadingEvents,
		visibleEventsCount: visibleEvents.length
	});

	const releaseSessionMutatationState = useMutationState({
		filters: { mutationKey: [MUTATION_KEY] },
		select: (mutation) => mutation.state.status
	})[0]; // useMutationState always returns an array

	const isCalendarDisabled = showInboundLeadNotice || searchFilters.groupSize === undefined;
	const isTimeSelectorDisabled = !searchFilters.date || isCalendarDisabled;

	const [searchParamSelected] = useSessionStorage(SEARCH_PARAM_SELECTED_KEY);

	useEffect(() => {
		if (featureFlags?.privateEvents) {
			setNoticeType(undefined);
		}
		else if (!searchFilters?.groupSize || !results || showInboundLeadNotice) {
			setNoticeType(undefined);
		}
		else if (!results?.metadata?.totalAvailable) {
			setNoticeType(AvailabilityLevel.NONE);
		}
		else if (results?.metadata?.groupSize?.length < TDR.LOW_INVENTORY_THRESHOLD) {
			setNoticeType(AvailabilityLevel.LOW);
		}
		else {
			setNoticeType(undefined);
		}
	}, [
		searchFilters?.groupSize,
		results?.metadata?.groupSize?.length,
		results?.metadata?.totalAvailable,
		showInboundLeadNotice
	]);

	const handleGroupSizeChange = (option: PartySizeOption) => {
		if (option.inboundRequest) {
			setShowInboundLeadNotice(true);
		}
		else {
			setShowInboundLeadNotice(false);
			updateSearchParams(
				{
					groupSize: option?.value ? Number(option.value) : undefined
				},
				{
					hash: HASH_ELEMENT_IDS.calendar
				}
			);
		}
	};

	const handleDateChange = (date: DateTime, availabilityPercent: number) => {
		const isSameDay = isToday(toLocalJsDate(date)) && availabilityPercent === 0;
		const TODAY = DateTime.now().setZone(restaurant?.timezone);
		const isPastHorizon = isDateBeyondHorizon(date, {
			policies: entityWithPolicies?.availabilityRestrictingPolicies,
			restaurant,
			now: TODAY
		});

		if (isSameDay) {
			setSameDayBookingOpen(true);
		}
		else if (availabilityPercent > 0) {
			setSameDayBookingOpen(false);
			setUnavailableDateOpen(false);
			if (date.toISODate() !== selectedDate) {
				analytics.track(FILTER_CHANGED, {
					filterName: 'date',
					filterValue: date.toISO(),
					sameDay: isSameDay,
					...(selectedTable
						? { searchFlow: 'find-a-table' }
						: {
							tablesShown: results?.metadata?.totalAvailable
						}),
					...filterCtx
				});
			}
		}
		else if (isPastHorizon) {
			setPastHorizonOpen(true);
		}
		else {
			setUnavailableDateOpen(true);
			setLastUnavailableDateClicked(date);
		}
	};

	const handleTimeslotChange = (newTime: string, price?: number) => {
		if (newTime !== searchFilters.time) {
			analytics.track(FILTER_CHANGED, {
				filterName: 'timeslot',
				filterValue: newTime,
				price,
				...(selectedTable
					? { searchFlow: 'find-a-table' }
					: {
						tablesShown: results?.metadata?.totalAvailable
					}),
				...filterCtx
			});
		}
		updateSearchParams({
			time: newTime ?? undefined
		});
		setPreviousTime(newTime || '');
	};

	const handleDayClick = (dateStr: string, hash?: string) => {
		updateSearchParams(
			{
				date: dateStr
			},
			{
				hash: hash
			}
		);
	};

	const handlePageExit = () => {
		analytics.track(PAGE_EXITED_EVENT, {
			page: selectedTable ? 'availability' : 'filters',
			currentFilters: {
				groupSize: searchFilters.groupSize,
				date: searchFilters.date,
				time: searchFilters.time
			},
			restaurantId: restaurant.id,
			tableId: selectedTable?.id,
			tableName: selectedTable?.name
		});
	};


	useEffect(() => {
		if (selectedDate !== searchFilters.date) {
			setIsSwitchingDate(isLoading);
		}
		if (searchFilters.date && !isLoading) {
			setSelectedDate(searchFilters.date);
		}
	}, [searchFilters.date, isLoading]);

	useEffect(() => {
		if (searchFilters.date && !isSwitchingDate && !isLoading) {
			return scrollToElement(timeSelectorRef, { delay: 100 });
		}
	}, [isSwitchingDate, isLoading, searchFilters.date]);

	useEffect(() => {
		if (!unavailableDateOpen) {
			setUnavailableMonth('');
		}
	}, [unavailableDateOpen]);

	useEffect(() => {
		const updateScreenStyles = () => {
			if (footerRef.current && searchContainerRef.current) {
				const height = footerRef.current.offsetHeight;
				const rect = searchContainerRef.current.getBoundingClientRect();
				footerRef.current.style.width = `${rect.width}px`;
				searchContainerRef.current.style.paddingBottom = `${height}px`;
			}
		};
		updateScreenStyles();
		window.addEventListener('resize', updateScreenStyles);
		return () => window.removeEventListener('resize', updateScreenStyles);

	}, [footerRef, searchContainerRef]);


	useEffect(() => {
		if (searchParamSelected) {
			const hasSpecialEvents = specialEventsVisibility.showSection;
			const targetRef = searchParamSelected === 'date' ? calendarRef :
				searchParamSelected === 'time' ? timeSelectorRef :
					groupSizeRef;

			return scrollToElement(targetRef, {
				delay: hasSpecialEvents && searchParamSelected === 'date' ? 200 : 100,
				withRetry: hasSpecialEvents && searchParamSelected === 'date'
			});
		}
	}, [searchParamSelected, specialEventsVisibility.showSection]);

	function getChangedParam(obj1: SearchFilters, obj2: SearchFilters): keyof SearchFilters | null {
		for (const key of Object.keys(obj1) as (keyof SearchFilters)[]) {
			if (obj1[key] !== obj2[key]) {
				return key;
			}
		}
		return null;
	}

	useEffect(() => {
		const changedKey = getChangedParam(prevFilters, searchFilters);
		if (changedKey === 'date') {
			return scrollToElement(timeSelectorRef, {
				withRetry: true,
				onSuccess: () => setPrevFilters(searchFilters)
			});
		}
		else if (changedKey === 'groupSize') {
			const hasSpecialEvents = specialEventsVisibility.showSection;
			return scrollToElement(calendarRef, {
				delay: hasSpecialEvents ? 200 : 0,
				withRetry: hasSpecialEvents,
				onSuccess: () => setPrevFilters(searchFilters)
			});
		}
	}, [searchFilters, specialEventsVisibility.showSection]);


	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			transition={{ duration: 0.4 }}
			className={`bg-tablz-dark-gray-1 relative ${searchFilters?.date ? 'h-fit' : 'h-full'} sm:w-[400px] w-full sm:h-full sm:overflow-y-auto flex flex-col`}
			ref={searchContainerRef}
			id='SearchContainer'
		>
			<header className="bg-tablz-dark-gray-2 border-b border-[#1F242F]">
				<SidebarHeader
					title={selectedTable ? selectedTable.name : restaurant.displayName || restaurant.name}
					buttonRight={
						<IconButton
							icon={X}
							to={{
								pathname: '..',
								search: initialSearch
							}}
							variant="secondary"
							className="bg-[#FFFFFF1A] text-[#D4D4D8] hover:bg-[#FFFFFF33]"
							onClick={handlePageExit}
						/>

					}
				/>
			</header>

			<section className="px-4 py-5 flex flex-col gap-6 grow" id='scrollableArea'>
				<div ref={groupSizeRef}>
					<PartySizePicker searchFilters={searchFilters}
						onChange={handleGroupSizeChange}
						options={groupSizeOptions}
						analyticsOption={
							selectedTable
								? { searchFlow: 'find-a-table' }
								: {
									tablesShown: results?.metadata?.totalAvailable
								}
						} />
				</div>
				{specialEventsVisibility.showSkeleton && <EventCarouselSkeleton />}

				{specialEventsVisibility.showBody && (
					<div className={styles.SpecialEvents}>
						<Subheading>{t('titles.special_events', { defaultValue: 'Special Events' })}</Subheading>
						<EventCarousel items={visibleEvents} />
					</div>
				)}

				{showInboundLeadNotice && !!featureFlags?.largeGroupInbounds?.enabled && (!!restaurant?.phoneNumber || !!restaurant?.contactEmail) ? (
					<DirectLGBInboundNotice maxGroupSize={getMaxSupportedGroupSize(groupSizeOptions)} restaurant={restaurant} />
				) : null}

				{notice?.(noticeType)}

				{!!searchFilters?.groupSize && <div ref={calendarRef}>
					<AvailabilityDatePicker
						disabled={showInboundLeadNotice || isCalendarDisabled}
						onChange={handleDateChange}
						filters={searchFilters}
						entityWithPolicies={entityWithPolicies}
						setPastHorizonOpen={setPastHorizonOpen}
						setUnavailableDateOpen={setUnavailableDateOpen}
						setUnavailableMonth={setUnavailableMonth}
						onDayClick={handleDayClick}
						tables={tables}
						restaurant={restaurant}
						featureFlags={featureFlags}
					/>
				</div>}

				<div id={HASH_ELEMENT_IDS.timeslots} className={isTimeSelectorDisabled ? 'pointer-events-none opacity-40' : ''}>
					{!showInboundLeadNotice &&
						!!searchFilters?.date &&
						(isSwitchingDate || !selectedDate || !tableIds ? (
							<TimeSelectorSkeleton />
						) : (
							<div ref={timeSelectorRef}>
								<TimeSelector
									restaurant={restaurant}
									table={selectedTable}
									searchFilters={searchFilters}
									tableIds={tableIds}
									isReleasingSession={releaseSessionMutatationState === 'pending'}
									onTimeslotChange={(value, price) => {
										handleTimeslotChange(value, price);
									}}
								/>
							</div>
						))}
				</div>
			</section>

			<footer className="bg-tablz-dark-gray-2 sticky w-full bottom-0 px-4 py-2 border-t border-tablz-med-gray-4 sm:rounded-b-2xl">
				{footer}
			</footer>

			{/* MODALS */}
			<SameDayBooking open={sameDayBookingOpen} onOpenChange={setSameDayBookingOpen} restaurant={restaurant} />
			<PastHorizon
				entityWithPolicies={entityWithPolicies}
				open={pastHorizonOpen}
				onOpenChange={setPastHorizonOpen}
				restaurant={restaurant}
			/>
			<UnavailableDate
				open={unavailableDateOpen}
				onOpenChange={setUnavailableDateOpen}
				date={lastUnavailableDateClicked}
				groupSize={searchFilters.groupSize}
				restaurant={restaurant}
				unavailableMonth={unavailableMonth}
				closedDateDescription={getClosedDateDescription(lastUnavailableDateClicked, restaurant)}
			/>
		</motion.div>
	);
};

export default SearchContainer;
